import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, FlatList, StyleSheet, TouchableOpacity, RefreshControl } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useFocusEffect } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import { useSharedValue, withRepeat, withTiming, Easing, cancelAnimation } from 'react-native-reanimated';
import BillBottomNav from '@/components/BillBottomNav';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import Avatar from '@/components/Avatar';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import FormattedText from '@/components/FormattedText';
import { SkeletonPost } from '@/components/Skeleton';
import CachedImage from '@/components/CachedImage';
import { communityService, CommunityPost } from '@/services/communityService';
import { supabase } from '@/lib/supabase';
import PublishingStatusBar from '@/components/PublishingStatusBar';
import CommunityMeetingsSection from '@/components/CommunityMeetingsSection';
import AsyncStorage from '@react-native-async-storage/async-storage';
import ActionBottomSheet from '@/components/ActionBottomSheet';
import Toast from '@/components/Toast';
import { useLanguage } from '@/contexts/LanguageContext';

export default function CommunityScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { unreadCount } = useNotifications();
  const { userProfile } = useAuth();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();
  const [activeTab, setActiveTab] = useState<'forYou' | 'following'>('forYou');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [posts, setPosts] = useState<CommunityPost[]>([]);
  const [publishingStatus, setPublishingStatus] = useState<'idle' | 'publishing' | 'error' | 'success'>('idle');
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState<CommunityPost | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const flatListRef = useRef<FlatList>(null);
  const rotation = useSharedValue(0);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const handlePostMenuPress = (post: CommunityPost) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setSelectedPost(post);
    setActionMenuVisible(true);
  };

  const handleDeletePost = async () => {
    if (!selectedPost) return;
    
    try {
      const success = await communityService.deletePost(selectedPost.id);
      if (success) {
        setPosts(posts.filter(p => p.id !== selectedPost.id));
        showToast(t('community.toast.postRemoved'), 'success');
      } else {
        showToast(t('community.toast.postRemoveError'), 'error');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      showToast(t('community.toast.postRemoveError'), 'error');
    }
    
    setActionMenuVisible(false);
    setSelectedPost(null);
  };

  const loadPosts = async (tab: 'forYou' | 'following' = 'forYou') => {
    try {
      if (tab === 'following') {
        const result = await communityService.getFollowingPosts({ page: 1, limit: 50 });
        setPosts(result.posts);
      } else {
        const result = await communityService.getPosts({
          topic: 'all',
          page: 1,
          limit: 50,
        });
        setPosts(result.posts);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  useEffect(() => {
    loadPosts();
    // PRD §5.7 — `community_feed_viewed`. Fired once on screen mount with
    // the default tab (`forYou`). Tab switches use `useFocusEffect` below
    // and intentionally don't re-fire — Clarity dedupes per session.
    import('@/utils/clarityEvents')
      .then(({ logCommunityFeedViewed }) => logCommunityFeedViewed({ tab: 'forYou' }))
      .catch(() => {});
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // P07 (PV2-07): Realtime channel — new posts are pushed in via Supabase
  // instead of reloading the full feed on every focus event. The channel
  // re-subscribes when the active tab changes so the correct feed is refreshed.
  useEffect(() => {
    const channel = supabase
      .channel(`community:posts:${activeTab}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'community_posts' },
        () => { loadPosts(activeTab); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeTab]);

  // On focus: only check for pending drafts that need publishing — the
  // realtime channel above handles live post delivery. Removed the
  // unconditional loadPosts() to stop re-fetching the entire feed on
  // every tab-switch or navigation-back event. (P07 / PV2-07)
  useFocusEffect(
    React.useCallback(() => {
      checkForPendingPost();
      checkForPendingEdit();
    }, [])
  );

  // Check if there's a pending post to publish
  const checkForPendingPost = async () => {
    try {
      const pendingPostJson = await AsyncStorage.getItem('pendingPost');
      if (pendingPostJson) {
        const postData = JSON.parse(pendingPostJson);
        await AsyncStorage.removeItem('pendingPost');
        
        setPublishingStatus('publishing');
        
        try {
          await communityService.createPost({
            content: postData.content,
            topics: postData.topics || [],
            is_anonymous: postData.is_anonymous || false,
          });

          // PRD §5.7 — `community_post_published`. Real publish path goes
          // through `communityService.createPost` (the legacy local-storage
          // `communityPostsService.addPost` only fires the macro
          // first-post event). Pass the first selected topic + a
          // length_bucket so the dashboard can split topic mix and
          // engagement depth without ever capturing post text.
          import('@/utils/clarityEvents')
            .then(({ logCommunityPostPublished, lengthBucket }) =>
              logCommunityPostPublished({
                topic_id: postData.topics?.[0],
                length_bucket: lengthBucket((postData.content || '').length),
              })
            )
            .catch(() => {});

          await loadPosts(activeTab);
          
          setPublishingStatus('success');
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          
          setTimeout(() => {
            setPublishingStatus('idle');
          }, 2000);
          
        } catch (error) {
          console.error('Error publishing post:', error);
          setPublishingStatus('error');
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
          
          setTimeout(() => {
            setPublishingStatus('idle');
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error checking for pending post:', error);
    }
  };

  // Check if there's a pending edit to process
  const checkForPendingEdit = async () => {
    try {
      const pendingEditJson = await AsyncStorage.getItem('pendingPostEdit');
      if (pendingEditJson) {
        const editData = JSON.parse(pendingEditJson);
        await AsyncStorage.removeItem('pendingPostEdit');
        
        setPublishingStatus('publishing');
        
        try {
          await communityService.updatePost(editData.postId, {
            content: editData.content,
            topics: editData.topics || [],
          });
          
          await loadPosts(activeTab);
          
          setPublishingStatus('success');
          showToast(t('community.toast.postUpdated'), 'success');
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
          
          setTimeout(() => {
            setPublishingStatus('idle');
          }, 2000);
          
        } catch (error) {
          console.error('Error updating post:', error);
          setPublishingStatus('error');
          showToast(t('community.toast.postUpdateError'), 'error');
          Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
          
          setTimeout(() => {
            setPublishingStatus('idle');
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error checking for pending edit:', error);
    }
  };

  const handleEditPost = () => {
    if (!selectedPost) return;
    
    setActionMenuVisible(false);
    
    router.push({
      pathname: '/community-new-post',
      params: {
        editMode: 'true',
        postId: selectedPost.id,
        existingContent: selectedPost.content,
        existingTopics: JSON.stringify(selectedPost.topics || []),
      },
    });
    
    setSelectedPost(null);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    
    rotation.value = withRepeat(
      withTiming(360, {
        duration: 1000,
        easing: Easing.linear,
      }),
      -1,
      false
    );

    await communityService.clearCache();
    await loadPosts(activeTab);
    
    setTimeout(() => {
      cancelAnimation(rotation);
      rotation.value = withTiming(0, { duration: 200 });
      setRefreshing(false);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    }, 500);
  };

  const handleMenuPress = () => {
    router.push('/perfil');
  };

  const handleNotificationPress = () => {
    router.push('/notifications');
  };

  const handleAddPost = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    router.push('/community-new-post');
  };

  const handleInterestsPress = () => {
    router.push('/interests-settings');
  };

  const handleTabChange = (tab: 'forYou' | 'following') => {
    Haptics.selectionAsync();
    setActiveTab(tab);
    setIsLoading(true);
    loadPosts(tab).finally(() => setIsLoading(false));
  };

  const formatTimestamp = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('community.timestamp.now');
    if (diffMins < 60) return `${diffMins}min`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return t('community.timestamp.yesterday');
    if (diffDays < 7) return `${diffDays}d`;
    
    const locale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  const PostCard = ({ post, isLast }: { post: CommunityPost; isLast: boolean }) => {
    const [isSupported, setIsSupported] = useState(post.user_has_supported);
    const [supportCount, setSupportCount] = useState(post.support_count);
    const [isUpdating, setIsUpdating] = useState(false);

    const authorName = post.is_anonymous ? t('community.author.anonymous') : (post.author?.full_name || t('community.author.default'));
    const authorPhoto = post.is_anonymous ? null : post.author?.avatar_url;
    const isVerified = !post.is_anonymous && post.author?.is_admin === true;

    const handleSupport = async (e: any) => {
      e.stopPropagation();
      if (isUpdating) return;
      
      setIsUpdating(true);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      
      const previousSupported = isSupported;
      const previousCount = supportCount;
      
      setIsSupported(!isSupported);
      setSupportCount(isSupported ? Math.max(0, supportCount - 1) : supportCount + 1);
      
      try {
        const result = await communityService.toggleSupport(post.id, previousSupported);
        setSupportCount(result.support_count);
        setIsSupported(result.user_has_supported);
        // PRD §5.7 — `community_post_liked`. Only fire on transitions
        // INTO the supported state — un-supporting isn't counted as a
        // like. `previousSupported === false` means we just liked it.
        if (!previousSupported && result.user_has_supported) {
          import('@/utils/clarityEvents')
            .then(({ logCommunityPostLiked }) =>
              logCommunityPostLiked({ post_id: post.id })
            )
            .catch(() => {});
        }
      } catch (error) {
        console.error('Error toggling support:', error);
        setIsSupported(previousSupported);
        setSupportCount(previousCount);
      } finally {
        setIsUpdating(false);
      }
    };

    const handleOpenPost = () => {
      router.push({
        pathname: '/community-post',
        params: {
          postId: post.id,
          postUserId: post.user_id,
          author: authorName,
          authorPhoto: authorPhoto || '',
          content: post.content,
          time: formatTimestamp(post.created_at),
          verified: isVerified ? 'true' : 'false',
          supportCount: supportCount.toString(),
          encourageCount: post.encourage_count.toString(),
          isSupported: isSupported ? 'true' : 'false',
        },
      });
    };

    const handleCommentPress = (e: any) => {
      e.stopPropagation();
      handleOpenPost();
    };

    const handleAvatarPress = (e: any) => {
      e.stopPropagation();
      if (post.is_anonymous) return;
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      router.push({
        pathname: '/community-profile',
        params: {
          userId: post.user_id,
          name: authorName,
          verified: isVerified ? 'true' : 'false',
        },
      });
    };
    
    return (
      <TouchableOpacity 
        style={[styles.postCard, !isLast && styles.postCardWithDivider]} 
        activeOpacity={0.7}
        onPress={handleOpenPost}
      >
        <TouchableOpacity onPress={handleAvatarPress} style={styles.avatarColumn}>
          <Avatar
            uri={authorPhoto}
            size={32}
            loading={false}
            showInitials={true}
            initials={authorName}
          />
        </TouchableOpacity>
        <View style={styles.postContent}>
          <View style={styles.userHeader}>
            <View style={styles.nameWithBadge}>
              <Text style={styles.authorName}>{authorName}</Text>
              {isVerified && (
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 4 }}>
                  <Path d="M8 0L9.79611 2.02786L12.4721 1.52786L12.9721 4.20389L15.5 5.5L14.5 8L15.5 10.5L12.9721 11.7961L12.4721 14.4721L9.79611 13.9721L8 16L6.20389 13.9721L3.52786 14.4721L3.02786 11.7961L0.5 10.5L1.5 8L0.5 5.5L3.02786 4.20389L3.52786 1.52786L6.20389 2.02786L8 0Z" fill="#6B9EC2"/>
                  <Path d="M5.5 8L7 9.5L10.5 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </Svg>
              )}
            </View>
            <View style={styles.headerRight}>
              <Text style={styles.timestamp}>{formatTimestamp(post.created_at)}</Text>
              {post.user_id === userProfile?.id && (
                <TouchableOpacity 
                  style={styles.postMenuButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    handlePostMenuPress(post);
                  }}
                >
                  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <Path d="M10 5.5C10.8284 5.5 11.5 4.82843 11.5 4C11.5 3.17157 10.8284 2.5 10 2.5C9.17157 2.5 8.5 3.17157 8.5 4C8.5 4.82843 9.17157 5.5 10 5.5Z" fill="#757575"/>
                    <Path d="M10 11.5C10.8284 11.5 11.5 10.8284 11.5 10C11.5 9.17157 10.8284 8.5 10 8.5C9.17157 8.5 8.5 9.17157 8.5 10C8.5 10.8284 9.17157 11.5 10 11.5Z" fill="#757575"/>
                    <Path d="M10 17.5C10.8284 17.5 11.5 16.8284 11.5 16C11.5 15.1716 10.8284 14.5 10 14.5C9.17157 14.5 8.5 15.1716 8.5 16C8.5 16.8284 9.17157 17.5 10 17.5Z" fill="#757575"/>
                  </Svg>
                </TouchableOpacity>
              )}
            </View>
          </View>
          <FormattedText 
            content={post.content}
            style={styles.noteContent}
          />
          <View style={styles.engagementSection}>
            <TouchableOpacity style={styles.engagementItem} onPress={handleSupport} disabled={isUpdating}>
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path 
                  d="M12.0017 5.92507C11.3979 5.24903 10.6225 4.72728 9.76648 4.42682C6.4652 3.25658 3.38047 5.59684 3.03157 8.87727C2.5431 13.8208 7.83511 16.8611 11.1366 19.572C11.5082 19.8819 12.0512 19.967 12.4989 19.7815C12.7138 19.7 12.8866 19.5452 13.0616 19.4014C16.3914 16.6725 21.6522 13.5358 20.9334 8.55669C20.401 5.07868 16.7703 3.12597 13.6389 4.67763C13.0203 4.98409 12.4606 5.40932 12.0017 5.92507Z" 
                  fill={isSupported ? "#DE5609" : "none"}
                  stroke={isSupported ? "#DE5609" : "#757575"}
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
              <Text style={styles.engagementCount}>{supportCount}</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.engagementItem} 
              onPress={handleCommentPress}
            >
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path 
                  fillRule="evenodd" 
                  clipRule="evenodd" 
                  d="M9.44015 18.0968L10.8048 19.4662C11.3597 20.0229 12.2573 20.0229 12.8112 19.4662L14.1767 18.0968C14.5653 17.707 15.0921 17.4886 15.6414 17.4886H16.6669C18.7555 17.4886 20.448 15.7895 20.448 13.6938V7.03212C20.448 4.93651 18.7555 3.23828 16.6669 3.23828H6.94996C4.86141 3.23828 3.16797 4.93651 3.16797 7.03212V13.6938C3.16797 15.7895 4.86141 17.4886 6.94996 17.4886H7.97554C8.52477 17.4886 9.05158 17.707 9.44015 18.0968Z" 
                  stroke="#757575" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
              <Text style={styles.engagementCount}>{post.encourage_count}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderPost = useCallback(({ item, index }: { item: CommunityPost; index: number }) => (
    <PostCard
      post={item}
      isLast={index === posts.length - 1}
    />
  ), [posts.length]);

  return (
    <SafeAreaView style={styles.container}>
      {/* Top Header */}
      <View style={styles.topHeader}>
        <TouchableOpacity 
          style={styles.avatarButton}
          onPress={handleMenuPress}
        >
          {hasPadrinhoPlus ? (
            <AvatarBlurRing>
              <Avatar
                uri={userProfile?.avatar_url || null}
                size={32}
                showInitials={true}
                initials={userProfile?.full_name || 'U'}
                borderWidth={0}
              />
            </AvatarBlurRing>
          ) : (
            <Avatar
              uri={userProfile?.avatar_url || null}
              size={32}
              showInitials={true}
              initials={userProfile?.full_name || 'U'}
              borderColor="#7E7D75"
              borderWidth={1}
            />
          )}
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('community.header.title')}</Text>
        <TouchableOpacity style={styles.notificationButton} onPress={handleNotificationPress}>
          <View style={styles.notificationIconContainer}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
            {unreadCount > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Feed Tabs */}
      <LinearGradient
        colors={['#F9F8F3', '#FFFFFF']}
        style={styles.tabsSection}
      >
        <View style={styles.tabsContainer}>
          {/* Add Interests Button */}
          <TouchableOpacity 
            style={styles.addInterestButton}
            onPress={handleInterestsPress}
          >
            <View style={styles.addInterestCircle}>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path 
                  d="M8 3.33334V12.6667" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <Path 
                  d="M3.33398 8H12.6673" 
                  stroke="white" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </Svg>
            </View>
          </TouchableOpacity>

          {/* For You Tab */}
          <View style={styles.tabWrapper}>
            <TouchableOpacity
              style={styles.interestTab}
              onPress={() => handleTabChange('forYou')}
            >
              <Text 
                style={[
                  styles.interestTabText,
                  activeTab === 'forYou' && styles.activeInterestTabText
                ]}
              >
                {t('community.tabs.forYou')}
              </Text>
            </TouchableOpacity>
            {activeTab === 'forYou' && (
              <View style={styles.activeTabIndicator} />
            )}
          </View>

          {/* Following Tab */}
          <View style={styles.tabWrapper}>
            <TouchableOpacity
              style={styles.interestTab}
              onPress={() => handleTabChange('following')}
            >
              <Text 
                style={[
                  styles.interestTabText,
                  activeTab === 'following' && styles.activeInterestTabText
                ]}
              >
                {t('community.tabs.following')}
              </Text>
            </TouchableOpacity>
            {activeTab === 'following' && (
              <View style={styles.activeTabIndicator} />
            )}
          </View>
        </View>
      </LinearGradient>

      {/* Publishing Status Bar */}
      {publishingStatus !== 'idle' && (
        <PublishingStatusBar 
          status={publishingStatus as 'publishing' | 'error' | 'success'} 
          visible={true}
        />
      )}

      {/* Posts Container with White Background */}
      <View style={styles.postsBodyContainer}>
        <FlatList
          ref={flatListRef}
          data={isLoading ? [] : posts}
          renderItem={renderPost}
          keyExtractor={(item) => item.id}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          removeClippedSubviews
          maxToRenderPerBatch={10}
          windowSize={5}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor="#669AB7"
              colors={['#669AB7']}
              progressViewOffset={0}
            />
          }
          ListHeaderComponent={<CommunityMeetingsSection />}
          ListEmptyComponent={
            isLoading ? (
              <View style={styles.postsContainer}>
                <SkeletonPost />
                <SkeletonPost />
                <SkeletonPost />
                <SkeletonPost />
                <SkeletonPost />
              </View>
            ) : (
              <View style={styles.emptyState}>
                <View style={styles.emptyStateContent}>
                  <View style={styles.emptyStateImageContainer}>
                    <CachedImage
                      source={require('../assets/images/painting.png')}
                      style={styles.emptyStateImage}
                      resizeMode="contain"
                    />
                  </View>
                  <Text style={styles.emptyStateTitle}>{t('community.emptyState.title')}</Text>
                  <Text style={styles.emptyStateSubtitle}>{t('community.emptyState.subtitle')}</Text>
                </View>
              </View>
            )
          }
        />
      </View>

      {/* Floating Add Post Button with Feather Icon */}
      <TouchableOpacity style={styles.floatingButton} onPress={handleAddPost}>
        <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <Path d="M3.5 20.6561C3.5 20.6561 3.99166 8.65565 17.0741 3.8347C28.2815 0.847713 14.6693 12.4665 14.6693 12.4665L9.06563 13.0168L11.6659 15.075C9.11398 16.8688 6.5631 17.7555 4.0112 17.7318" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <Path opacity="0.4" d="M11.2168 20.6562H17.6269" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </Svg>
      </TouchableOpacity>

      {/* Bottom Navigation */}
      <BillBottomNav />

      {/* Action Menu Bottom Sheet */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: actionMenuVisible ? 'auto' : 'none' }}>
        <ActionBottomSheet
          isVisible={actionMenuVisible}
          onClose={() => setActionMenuVisible(false)}
          title={t('community.actions.options')}
        >
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
              marginBottom: 8,
            }} 
            onPress={handleEditPost}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M6.77914 16.6022L8.16793 16.1982C8.9144 15.9807 9.56473 15.516 10.0113 14.8807L15.3353 7.49803C15.9726 6.62359 15.7804 5.39908 14.906 4.76171L13.4614 3.70856C12.5871 3.07228 11.3615 3.26471 10.7251 4.13793L5.34262 11.6032C4.93187 12.1882 4.71484 12.8868 4.72082 13.602L4.73417 15.0818C4.74426 16.1408 5.7625 16.8977 6.77914 16.6022Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('community.actions.editPost')}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: 16,
              backgroundColor: '#F9F8F3',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#F0EEE4',
              gap: 16,
            }} 
            onPress={handleDeletePost}
          >
            <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <Path d="M4.10352 8.11298L4.57425 14.5558C4.67154 15.8894 5.78214 16.9221 7.11875 16.9221H11.7289C13.0663 16.9221 14.1762 15.8894 14.2735 14.5565L14.7442 8.11298" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M15.7814 5.6926H3.06641" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M12.3246 5.69328L11.9377 3.78865C11.8255 3.36881 11.4445 3.07694 11.0105 3.07694H7.84031C7.40399 3.07544 7.02157 3.36731 6.90856 3.78865L6.52539 5.69328" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.96289 9.8317V13.2997M10.5632 9.8317V13.2997" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.actionCardText}>{t('community.actions.deletePost')}</Text>
          </TouchableOpacity>
        </ActionBottomSheet>
      </View>

      {/* Toast */}
      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onHide={() => setToastVisible(false)}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  topHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 24,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 15,
    backgroundColor: '#F9F8F3',
  },
  avatarButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationButton: {
    padding: 4,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#E53E3E',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  notificationBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  tabsSection: {
    paddingLeft: 24,
    height: 65,
    borderBottomWidth: 1,
    borderBottomColor: '#F9F8F3',
  },
  tabsScrollContent: {
    alignItems: 'center',
    gap: 24,
    paddingRight: 24,
  },
  tabsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    paddingRight: 24,
    height: 65,
  },
  addInterestButton: {
    justifyContent: 'center',
    alignItems: 'flex-end',
    height: 65,
  },
  addInterestCircle: {
    backgroundColor: '#151515',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabWrapper: {
    position: 'relative',
    height: 65,
  },
  interestTab: {
    paddingTop: 24,
    paddingBottom: 24,
    justifyContent: 'center',
    alignItems: 'center',
    height: 65,
  },
  interestTabText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#757575',
  },
  activeInterestTabText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    lineHeight: 19.6,
    color: '#151515',
  },
  activeTabIndicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: '#151515',
  },
  postsBodyContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
  },
  postsContainer: {
    paddingTop: 0,
    paddingBottom: 24,
  },
  postCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 12,
    backgroundColor: '#FFFFFF',
  },
  postCardWithDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#F0EEE4',
  },
  avatarColumn: {
    alignItems: 'center',
  },
  postContent: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 8,
  },
  userHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  nameWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  postMenuButton: {
    padding: 4,
  },
  actionCardText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  authorName: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  timestamp: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#757C85',
    lineHeight: 12,
  },
  noteContent: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 19.6,
  },
  engagementSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  engagementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  engagementCount: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#757575',
    lineHeight: 19.6,
  },
  floatingButton: {
    position: 'absolute',
    bottom: 112,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: '#151515',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 50,
  },
  emptyState: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 80,
  },
  emptyStateContent: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    flexDirection: 'column',
    width: '100%',
  },
  emptyStateImageContainer: {
    width: 280,
    height: 280,
  },
  emptyStateImage: {
    width: '100%',
    height: '100%',
  },
  emptyStateTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
  },
  emptyStateSubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
});
